// ==========================================================================
// Squarebit Store - Copyright (c) 2026 Squarebit LLC. All rights reserved.
// Proprietary and confidential. Not for redistribution.
// ==========================================================================

import React from 'react';
import DocsLayout, {
  NavSection
} from '../../common/components/docs-layout.component';
import { NotFound } from '../../common/components/404.component';
import { useHasFrameworkAccess } from '../feature/use-sbframework-access';

/**
 * The framework's documentation area, laid out like every other product's.
 *
 * Gated on the grant, the same as the downloads. Rendered in place rather than
 * redirected to: a redirect rewrites the URL, so a holder who lands here while
 * the session is still resolving ends up parked on /not-found with no way back
 * to the page they asked for.
 *
 * The prose on these pages is written in English rather than through i18n.
 * They are long-form documentation for an invite-only product, the guides ship
 * in the module in English, and nine locale copies of the same English would be
 * parity theatre rather than translation. Short UI strings elsewhere still go
 * through t().
 */
const SbFrameworkDocsLayout: React.FC = () => {
  const { granted, isLoading } = useHasFrameworkAccess();

  const NAV_SECTIONS: NavSection[] = [
    {
      title: 'Squarebit Framework',
      items: [
        { label: 'Overview', to: '/sbframework/docs' },
        {
          label: 'Getting started',
          children: [
            {
              label: 'Install and license',
              to: '/sbframework/docs/install',
              subItems: [
                { label: 'Steps', anchor: 'steps' },
                { label: 'Installing it by hand', anchor: 'by-hand' },
                { label: 'If something is not right', anchor: 'troubleshooting' }
              ]
            }
          ]
        },
        {
          label: 'Guides',
          children: [
            {
              label: 'Templates and builds',
              to: '/sbframework/docs/templates',
              subItems: [
                { label: 'What a template is', anchor: 'what-a-template-is' },
                { label: 'The library', anchor: 'the-library' },
                { label: 'How a build runs', anchor: 'how-a-build-runs' },
                { label: 'Running a build', anchor: 'running-a-build' },
                { label: 'Layering your own', anchor: 'layering' }
              ]
            },
            {
              label: 'nxt',
              to: '/sbframework/docs/nxt',
              subItems: [
                { label: 'What it is', anchor: 'what-it-is' },
                { label: 'Ideas worth knowing', anchor: 'ideas' },
                { label: 'Tokens', anchor: 'tokens' },
                { label: 'Installing it', anchor: 'installing' }
              ]
            }
          ]
        },
        {
          label: 'API reference',
          children: [
            // First, because most people arrive knowing the name of the thing
            // they want rather than which package holds it.
            { label: 'Search the API', to: '/sbframework/docs/api' },
            { label: 'core', to: '/sbframework/docs/api/core' },
            { label: 'mix', to: '/sbframework/docs/api/mix' },
            { label: 'sbgui', to: '/sbframework/docs/api/sbgui' }
          ]
        }
      ]
    }
  ];

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-[80px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }
  if (!granted) return <NotFound />;

  return <DocsLayout navSections={NAV_SECTIONS} />;
};

export default SbFrameworkDocsLayout;
